/**
 * Best Combo Card Component
 * Highlights the winning combination with maximum savings
 */

import { TrendingDown, ArrowRight } from 'lucide-react';
import type { Combo } from '../types';
import { MILEAGE_PROGRAM_LABELS } from '../types';
import {
  formatCurrency,
  formatPercentage,
  formatMiles,
} from '../utils/calculations';

interface BestComboCardProps {
  combo: Combo;
  airlinePrice: number;
}

export function BestComboCard({ combo, airlinePrice }: BestComboCardProps) {
  return (
    <div className="relative pt-4">

      {/* Main Card */}
      <div className="relative bg-gradient-to-br from-zinc-900/90 to-zinc-900/60 border-2 border-[#B05D41]/50 rounded-xl p-6 sm:p-8 shadow-2xl shadow-[#B05D41]/20">
        {/* Badge */}
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-[#B05D41] to-[#8B4A33] rounded-full shadow-lg">
            <span className="text-sm font-bold text-white uppercase tracking-wide">
              Melhor Combinação
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="mt-4 space-y-6">

          {/* Programs Used */}
          <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800/50">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {/* Outbound */}
              <div className="flex-1 text-center sm:text-left">
                <p className="text-xs text-zinc-400 mb-1 uppercase tracking-wide">
                  Ida
                </p>
                <div className="space-y-0.5">
                  <p className="text-lg font-bold text-[#B05D41]">
                    {MILEAGE_PROGRAM_LABELS[combo.outbound.program]}
                  </p>
                  <p className="text-sm text-zinc-300">
                    {formatMiles(combo.outbound.miles)} milhas
                  </p>
                  <p className="text-xs text-zinc-500">
                    {formatCurrency(combo.outbound.costPerThousand)}/mil
                  </p>
                </div>
              </div>

              {/* Arrow */}
              {combo.return && (
                <div className="hidden sm:block">
                  <ArrowRight className="w-6 h-6 text-[#B05D41]" />
                </div>
              )}

              {/* Return */}
              {combo.return && (
                <div className="flex-1 text-center sm:text-right">
                  <p className="text-xs text-zinc-400 mb-1 uppercase tracking-wide">
                    Volta
                  </p>
                  <div className="space-y-0.5">
                    <p className="text-lg font-bold text-blue-400">
                      {MILEAGE_PROGRAM_LABELS[combo.return.program]}
                    </p>
                    <p className="text-sm text-zinc-300">
                      {formatMiles(combo.return.miles)} milhas
                    </p>
                    <p className="text-xs text-zinc-500">
                      {formatCurrency(combo.return.costPerThousand)}/mil
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cost Comparison */}
          <div className="grid grid-cols-2 gap-4">
            {/* Airline Price */}
            <div className="text-center p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
              <p className="text-xs text-zinc-400 mb-1 uppercase tracking-wide">
                Preço Cia Aérea
              </p>
              <p className="text-xl font-bold text-zinc-300">
                {formatCurrency(airlinePrice)}
              </p>
            </div>

            {/* Combo Cost */}
            <div className="text-center p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <p className="text-xs text-emerald-400 mb-1 uppercase tracking-wide">
                Custo Milhas
              </p>
              <p className="text-xl font-bold text-emerald-400">
                {formatCurrency(combo.totalCost)}
              </p>
            </div>
          </div>

          {/* Savings Highlight */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 rounded-lg blur-xl" />
            <div className="relative bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-lg p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <TrendingDown className="w-6 h-6 text-emerald-400" />
                <p className="text-sm font-medium text-emerald-400 uppercase tracking-wide">
                  Sua margem de lucro
                </p>
              </div>
              <p className="text-4xl sm:text-5xl font-black text-emerald-400 mb-1">
                {formatCurrency(combo.savings)}
              </p>
              <p className="text-lg text-emerald-300">
                {formatPercentage(combo.savingsPercentage)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
